import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { format } from 'date-fns';
import { getTokenUsage } from './actions';
import { useSelector } from 'react-redux';
import { Box, Button, Typography, Paper, Grid } from '@mui/material';
import images from '../../Utils/images';
import colors from '../../Utils/colors';
import { styledSx } from '../ContactModule/Tags/variableDependencies';

export default function TokenUsagePage() {
    const { agentId } = useParams<{ agentId: string }>();
    const navigate = useNavigate();
    const authtoken = useSelector((state: any) => state.main.auth_token);
    const localdata = useSelector((state: any) => state.main.localdata);
    const currencySymbol = localdata?.walletCurrency === "INR" ? "₹" : "$";

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [usageData, setUsageData] = useState<any[]>([]);
    const [totalStats, setTotalStats] = useState({
        totalInputTokens: 0,
        totalOutputTokens: 0,
        totalTokens: 0,
        totalTokenCost: 0,
        totalRecords: 0,
    });

    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 50,
    });

    const fetchTokenUsage = async () => {
        if (!agentId) return;

        setLoading(true);
        setError(null);

        try {
            const response = await getTokenUsage(
                agentId,
                {
                    page: paginationModel.page + 1,
                    limit: paginationModel.pageSize,
                },
                authtoken
            );

            const data = response.data?.data;

            const rows = data.records.map((item: any, index: number) => ({
                id: `${item.timestamp}-${item.model}-${index}`,
                timestamp: item.timestamp,
                model: item.model,
                input_tokens: item.input_tokens,
                output_tokens: item.output_tokens,
                token_cost: item.token_cost,
            }));



            setUsageData(rows);
            setTotalStats({
                ...data.totalStatsAggregate,
                totalRecords: data.totalRecords || 0,
            });
        } catch (err) {
            console.error('Error fetching token usage:', err);
            setError('Failed to fetch token usage data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (agentId) {
            fetchTokenUsage();
        }
    }, [agentId, paginationModel.page, paginationModel.pageSize]);

    const columns: GridColDef[] = [
        {
            field: 'timestamp',
            headerName: 'Timestamp',
            flex: 1,
            align: 'center',
            headerAlign: 'center',
            valueFormatter: (params) =>
                format(new Date(params.value), 'yyyy-MM-dd HH:mm:ss'),
        },
        { field: 'model', headerName: 'Model', flex: 1, align: 'center', headerAlign: 'center' },
        {
            field: 'input_tokens',
            headerName: 'Input Tokens',
            flex: 1,
            align: 'center',
            headerAlign: 'center',
            type: 'number',
            valueFormatter: (params) =>
                params.value?.toLocaleString() || '0',
        },
        {
            field: 'output_tokens',
            headerName: 'Output Tokens',
            flex: 1,
            align: 'center',
            headerAlign: 'center',
            type: 'number',
            valueFormatter: (params) =>
                params.value?.toLocaleString() || '0',
        },
        {
            field: 'token_cost',
            headerName: `Cost (${currencySymbol})`,
            flex: 1,
            align: 'center',
            headerAlign: 'center',
            type: 'number',
            valueFormatter: (params) =>
                `${currencySymbol}${params.value?.toFixed(12) || '0.0000'}`,
        },
    ];

    return (
        <Box sx={{ px: { xs: 2, sm: 4 }, py: 2, background: '#fcfcfc', minHeight: '100vh', width: '100%' }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Button variant="text" sx={{ textTransform: 'none', color: '#222', fontWeight: 700, fontSize: 18, justifyContent: 'flex-start', pl: 0 }} startIcon={<img src={images.BackArrowBlack} alt="Back" style={{ width: 22, height: 22 }} />} onClick={() => navigate('/creator/ai-assistants')}>
                    Token Usage
                </Button>
                <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" color="text.secondary">
                        Agent ID: {agentId}
                    </Typography>
                </Box>
            </Box>

            <Box mb={2}>
                <Paper
                    elevation={0}
                    sx={{
                        backgroundColor: colors.primary01,
                        px: 3,
                        py: 1.5,
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        color: colors.primary04,
                        fontWeight: 500,
                        fontSize: 14,
                    }}
                >
                    <Typography component="span" sx={{ fontSize: 18 }}>
                        ℹ️
                    </Typography>
                    <Typography variant="body2">
                        Data refreshes every 3 hours to ensure up-to-date token usage statistics.
                    </Typography>
                </Paper>
            </Box>

            {/* Stats */}
            <Paper elevation={1} sx={{ p: { xs: 2, sm: 3 }, mb: 3, borderRadius: 2 }}>
                <Grid container spacing={2}>
                    <Grid item xs={6} sm={3}>
                        <Typography variant="subtitle2" color="text.secondary">Input Tokens</Typography>
                        <Typography variant="h6">{totalStats.totalInputTokens?.toLocaleString?.() || 0}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <Typography variant="subtitle2" color="text.secondary">Output Tokens</Typography>
                        <Typography variant="h6">{totalStats.totalOutputTokens?.toLocaleString?.() || 0}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <Typography variant="subtitle2" color="text.secondary">Total Tokens</Typography>
                        <Typography variant="h6">{totalStats.totalTokens?.toLocaleString?.() || 0}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <Typography variant="subtitle2" color="text.secondary">Total Cost ({currencySymbol})</Typography>
                        <Typography variant="h6">{currencySymbol}{totalStats.totalTokenCost?.toFixed?.(12) || '0.0000'}</Typography>
                    </Grid>
                </Grid>
            </Paper>

            {/* DataGrid */}
            <Paper elevation={1} sx={{ borderRadius: 2 }}>
                <Box sx={{ width: '100%', height: { xs: 500, sm: 600 } }}>
                    <DataGrid
                        rows={usageData}
                        columns={columns}
                        loading={loading}
                        pagination
                        paginationModel={paginationModel}
                        onPaginationModelChange={setPaginationModel}
                        rowCount={totalStats.totalRecords || 0} // if needed
                        paginationMode="server"
                        disableRowSelectionOnClick
                        sx={styledSx}
                    />
                </Box>
            </Paper>

            {/* Error Message */}
            {error && (
                <Box
                    sx={{
                        mt: 2,
                        p: 2,
                        backgroundColor: '#ffebee',
                        color: '#d32f2f',
                        borderRadius: 1,
                    }}
                >
                    {error}
                </Box>
            )}
        </Box>
    );
}
